import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

// ----------------- utils -----------------
function cn(...c) {
  return c.filter(Boolean).join(" ");
}
function pad2(n) {
  return String(n).padStart(2, "0");
}
function ymString(year, month1to12) {
  return `${year}-${pad2(month1to12)}`;
}
function ymdStringCompact(year, month1to12, day) {
  return `${year}${pad2(month1to12)}${pad2(day)}`;
}
function dateAtMidnight(year, month1to12, day) {
  return new Date(year, month1to12 - 1, day, 0, 0, 0, 0);
}
function daysInMonth(year, month1to12) {
  return new Date(year, month1to12, 0).getDate();
}
function weekday0Sun(d) {
  return d.getDay(); // 0 Sun .. 6 Sat
}
function nowYear() {
  return new Date().getFullYear();
}
function isoDate(d) {
  // yyyy-mm-dd in local time
  const y = d.getFullYear();
  const m = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  return `${y}-${m}-${day}`;
}
function fromIsoDateLocal(iso) {
  const [y, m, d] = String(iso).split("-").map((x) => Number(x));
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}
function toIsoDateLocal(d) {
  return isoDate(d);
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
function monthOptionLabel(m1to12) {
  const m = Number(m1to12);
  const name = MONTHS[m - 1] || `Month ${m}`;
  return `${name} - ${pad2(m)}`;
}

// ----------------- week helpers (Monday-start) -----------------
function startOfWeekMon(date) {
  const d = new Date(date);
  const dow = d.getDay(); // 0 Sun .. 6 Sat
  const diff = dow === 0 ? -6 : 1 - dow; // move back to Monday
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function buildMonthWeeksMonStart(year, month1to12) {
  const first = dateAtMidnight(year, month1to12, 1);
  const last = dateAtMidnight(year, month1to12, daysInMonth(year, month1to12));

  const start = startOfWeekMon(first);
  const weeks = [];

  let cursor = new Date(start);
  let idx = 0;

  while (true) {
    const days = [];
    let anyInMonth = false;

    for (let i = 0; i < 7; i++) {
      const d = new Date(cursor);
      d.setDate(cursor.getDate() + i);
      d.setHours(0, 0, 0, 0);

      const inMonth = d.getFullYear() === year && d.getMonth() + 1 === month1to12;
      if (inMonth) anyInMonth = true;
      days.push({ date: d, inMonth });
    }

    // stop once we're past last day and the week contains no in-month days
    if (!anyInMonth && cursor > last) break;

    weeks.push({
      weekIndex: idx,
      weekStart: new Date(cursor),
      days,
      anyInMonth,
    });

    cursor.setDate(cursor.getDate() + 7);
    idx++;
    if (idx > 8) break; // safety
  }

  return weeks;
}

// ----------------- scheduling fields -----------------
function ensureTaskScheduling(s) {
  const y = nowYear();
  const year = Number(s?.year) || y;
  const months = s?.months && typeof s.months === "object" ? s.months : {};
  return {
    v: 1,
    year,
    months,
    lastScheduledAt: s?.lastScheduledAt ?? null,
    lastScheduledMonth: s?.lastScheduledMonth ?? null,
    lastEngineerId: s?.lastEngineerId ?? null,
    lastEngineerName: s?.lastEngineerName ?? null,
    lastScheduleChoice: s?.lastScheduleChoice ?? null,
  };
}

function isMonthLocked(task, year, month1to12) {
  const s = ensureTaskScheduling(task?.scheduling);
  if (Number(s.year) !== Number(year)) return false;
  return s.months?.[pad2(month1to12)] === true;
}

function isTaskApplicableInMonth(task, year, month) {
  const type = task?.cadence?.type || "monthly";

  if (type === "quarterly") {
    const qMonths = Array.isArray(task?.cadence?.quarterMonths)
      ? task.cadence.quarterMonths.map(Number)
      : [];
    return qMonths.includes(Number(month));
  }

  if (type === "yearly") {
    // you said month predetermined, date chosen at scheduling time
    return Number(task?.cadence?.month) === Number(month);
  }

  return true; // daily/weekly/monthly always applicable
}

function lockMonthOnTask(task, year, month1to12, engineer, scheduleChoice) {
  const s = ensureTaskScheduling(task?.scheduling);
  const months = Number(s.year) === Number(year) ? { ...(s.months || {}) } : {};
  months[pad2(month1to12)] = true;

  // IMPORTANT: do NOT use serverTimestamp() inside arrays; tasks is an array.
  // Use Timestamp.now() for fields stored inside tasks[].
  return {
    ...task,
    scheduling: {
      ...s,
      year,
      months,
      lastScheduledAt: Timestamp.now(),
      lastScheduledMonth: ymString(year, month1to12),
      lastEngineerId: engineer?.id || null,
      lastEngineerName: engineer?.name || null,
      lastScheduleChoice: scheduleChoice || null,
    },
  };
}

// ----------------- due date generation -----------------
function dueDatesForDaily(task, year, month) {
  const dim = daysInMonth(year, month);
  const out = [];
  const dows = Array.isArray(task?.cadence?.daysOfWeek)
    ? task.cadence.daysOfWeek
        .map(Number)
        .filter((x) => [0, 1, 2, 3, 4, 5, 6].includes(x))
    : null;

  for (let day = 1; day <= dim; day++) {
    const d = dateAtMidnight(year, month, day);
    if (!dows || dows.includes(weekday0Sun(d))) out.push(d);
  }
  return out;
}

// weekly uses weekSelections (one per required week)
function weekSelectionsToDates(weekSelections, requiredWeekIndexes) {
  if (!weekSelections || typeof weekSelections !== "object") return [];
  const req = Array.isArray(requiredWeekIndexes) ? requiredWeekIndexes.map(String) : null;

  const entries = Object.entries(weekSelections)
    .filter(([wk, iso]) => Boolean(iso) && (!req || req.includes(String(wk))))
    .map(([, iso]) => fromIsoDateLocal(iso));

  entries.sort((a, b) => a - b);
  return entries;
}

// monthly/quarterly/yearly use single ISO date
function singleIsoToDates(singleIso) {
  if (!singleIso) return [];
  return [fromIsoDateLocal(singleIso)];
}

// ----------------- UI bits -----------------
function Button({ className, ...props }) {
  return (
    <button
      {...props}
      className={cn(
        "bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white font-semibold",
        "px-4 py-2 rounded-lg transition",
        className
      )}
    />
  );
}
function GhostButton({ className, ...props }) {
  return (
    <button
      {...props}
      className={cn(
        "bg-white hover:bg-gray-50 disabled:bg-gray-50 text-gray-900 font-semibold",
        "px-4 py-2 rounded-lg border border-gray-200 transition",
        className
      )}
    />
  );
}
function Select({ className, ...props }) {
  return (
    <select
      {...props}
      className={cn(
        "w-full rounded-lg border border-gray-200 bg-white px-3 py-2",
        "focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-300",
        className
      )}
    />
  );
}

// ----------------- main component -----------------
export default function PPMScheduleJobs() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [assets, setAssets] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [selectedAssetId, setSelectedAssetId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  // panel state
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelTaskId, setPanelTaskId] = useState(null);
  const [selectedEngineerId, setSelectedEngineerId] = useState("");

  // ✅ NEW: bulk scheduling
  const [bulkTaskIds, setBulkTaskIds] = useState([]); // when non-empty, schedule these tasks together
  const isBulk = bulkTaskIds.length > 0;

  // ✅ NEW: weekly omit-week option (fix month-starts-on-weekend forcing Sunday)
  const [skippedWeeks, setSkippedWeeks] = useState({}); // { [weekIndex]: true }

    // weekly calendar selections: { [weekIndex]: "YYYY-MM-DD" }
  const [weekSelections, setWeekSelections] = useState({});
  // for monthly/quarterly/yearly: single selected date for the month
  const [singleDateIso, setSingleDateIso] = useState("");

  // bi-weekly: 0 means weeks 0,2,4... allowed; 1 means weeks 1,3,5... allowed
  const [biWeeklyPhase, setBiWeeklyPhase] = useState(0);

  const ym = useMemo(() => ymString(year, month), [year, month]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const [assetsSnap, engSnap] = await Promise.all([
          getDocs(collection(db, "ppmassets")),
          getDocs(collection(db, "ppmengineers")),
        ]);

        const a = assetsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const e = engSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((x) => x && x.active !== false);

        if (cancelled) return;

        setAssets(a);
        setEngineers(e);
        setSelectedAssetId((prev) => prev || a[0]?.id || null);
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Failed to load assets/engineers.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedAsset = useMemo(
    () => assets.find((a) => a.id === selectedAssetId) || null,
    [assets, selectedAssetId]
  );

  const tasks = useMemo(() => {
    const raw = Array.isArray(selectedAsset?.tasks) ? selectedAsset.tasks : [];
    return raw
      .map((t) => ({ ...t, scheduling: ensureTaskScheduling(t?.scheduling) }))
      .filter((t) => t && t.id && t.active !== false)
      .filter((t) => isTaskApplicableInMonth(t, year, month));
  }, [selectedAsset, year, month]);

  const unscheduledTasks = useMemo(() => {
    return tasks.filter((t) => !isMonthLocked(t, year, month));
  }, [tasks, year, month]);

  const panelTask = useMemo(
    () => tasks.find((t) => t.id === panelTaskId) || null,
    [tasks, panelTaskId]
  );

  // ✅ NEW: when bulk mode, schedule multiple tasks in one go
  const panelTasks = useMemo(() => {
    if (bulkTaskIds.length) {
      const set = new Set(bulkTaskIds.map(String));
      return tasks.filter((t) => set.has(String(t.id)));
    }
    return panelTask ? [panelTask] : [];
  }, [bulkTaskIds, tasks, panelTask]);

  const suggestedEngineer = useMemo(() => {
    const t = panelTasks[0];
    if (!t) return null;
    const id = t?.scheduling?.lastEngineerId;
    if (!id) return null;
    return engineers.find((e) => e.id === id) || null;
  }, [panelTasks, engineers]);

  // calendar weeks grid (Mon start)
  const monthWeeks = useMemo(() => buildMonthWeeksMonStart(year, month), [year, month]);

  // weekly interval: 1=weekly, 2=bi-weekly
  const weeklyInterval = useMemo(() => {
    const i = Number(panelTask?.cadence?.interval);
    return [1, 2].includes(i) ? i : 1;
  }, [panelTask]);

  // eligible weeks are those containing any in-month day
  const eligibleWeekIndexes = useMemo(() => {
    return monthWeeks.filter((w) => w.anyInMonth).map((w) => w.weekIndex);
  }, [monthWeeks]);

  // --- BI-WEEKLY "COMPLETION PROBLEM" FIX ---
  // Required weeks depends on biWeeklyPhase, so include it in deps.
  function isWeekAllowed(weekIndex) {
    if (weeklyInterval !== 2) return true;
    // phase 0: allow even weekIndex; phase 1: allow odd weekIndex
    return (Number(weekIndex) + Number(biWeeklyPhase)) % 2 === 0;
  }

  const requiredWeekIndexes = useMemo(() => {
    if (!panelTask || panelTask.cadence?.type !== "weekly") return [];
    return eligibleWeekIndexes.filter((wi) => isWeekAllowed(wi));
  }, [panelTask, eligibleWeekIndexes, weeklyInterval, biWeeklyPhase]); // ✅ includes biWeeklyPhase

  function isWeekComplete(weekIndex) {
    return Boolean(weekSelections?.[String(weekIndex)]);
  }

  function selectDateForWeek(weekIndex, dateObj) {
    setWeekSelections((prev) => ({
      ...(prev || {}),
      [String(weekIndex)]: isoDate(dateObj),
    }));
  }

  function clearWeekSelection(weekIndex) {
    setWeekSelections((prev) => {
      const next = { ...(prev || {}) };
      delete next[String(weekIndex)];
      return next;
    });
  }

    function toggleSkipWeek(weekIndex) {
    setSkippedWeeks((prev) => {
      const next = { ...(prev || {}) };
      const k = String(weekIndex);
      if (next[k]) delete next[k];
      else next[k] = true;
      return next;
    });

    // if skipping, also clear any date selection for that week
    clearWeekSelection(weekIndex);
  }

  function isWeekSkipped(weekIndex) {
    return Boolean(skippedWeeks?.[String(weekIndex)]);
  }

  function weeklyIntervalForTask(task) {
    const i = Number(task?.cadence?.interval);
    return [1, 2].includes(i) ? i : 1;
  }

  function requiredWeeksForTask(task) {
    // weeks that contain any in-month day
    const eligible = eligibleWeekIndexes;

    if (!task || task?.cadence?.type !== "weekly") return [];

    const interval = weeklyIntervalForTask(task);
    if (interval !== 2) return eligible;

    // bi-weekly uses the current panel biWeeklyPhase for all bi-weekly tasks
    return eligible.filter((wi) => (Number(wi) + Number(biWeeklyPhase)) % 2 === 0);
  }

  function buildScheduleChoiceForTask(task) {
    const type = task?.cadence?.type || "monthly";

    if (type === "daily") {
      return { v: 1, kind: "daily-auto" };
    }

    if (type === "weekly") {
      const requiredWeeks = requiredWeeksForTask(task);

      // allow omitted weeks
      const selectedDates = requiredWeeks
        .filter((wi) => !isWeekSkipped(wi))
        .map((wi) => weekSelections?.[String(wi)])
        .filter(Boolean);

      const patternWeekdays = selectedDates.map((ymd) => weekday0Sun(fromIsoDateLocal(ymd)));

      return {
        v: 1,
        kind: "weekly-calendar",
        interval: weeklyIntervalForTask(task),
        biWeeklyPhase: weeklyIntervalForTask(task) === 2 ? biWeeklyPhase : null,
        requiredWeeks,
        skippedWeeks: requiredWeeks.filter((wi) => isWeekSkipped(wi)),
        selectedDates,
        patternWeekdays,
      };
    }

    if (type === "monthly") {
      return { v: 1, kind: "monthly-single", selectedDates: singleDateIso ? [singleDateIso] : [] };
    }
    if (type === "quarterly") {
      return { v: 1, kind: "quarterly-single", selectedDates: singleDateIso ? [singleDateIso] : [] };
    }
    if (type === "yearly") {
      return { v: 1, kind: "yearly-single", selectedDates: singleDateIso ? [singleDateIso] : [] };
    }

    return { v: 1, kind: "unknown" };
  }

  function dueDatesForTask(task) {
    const type = task?.cadence?.type || "monthly";

    if (type === "daily") return dueDatesForDaily(task, year, month);

    if (type === "weekly") {
      const requiredWeeks = requiredWeeksForTask(task).filter((wi) => !isWeekSkipped(wi));
      return weekSelectionsToDates(weekSelections, requiredWeeks);
    }

    if (type === "monthly" || type === "quarterly" || type === "yearly") {
      return singleIsoToDates(singleDateIso);
    }

    return [];
  }

  function openTaskPanel(taskId) {
    setErr("");
    setMsg("");

    const t = tasks.find((x) => x.id === taskId);
    if (!t) return;

    if (isMonthLocked(t, year, month)) {
      setErr("That task is already scheduled for this month.");
      return;
    }

    setPanelTaskId(taskId);
    setPanelOpen(true);

    setSelectedEngineerId(t?.scheduling?.lastEngineerId || "");

    // restore last phase if present (only matters for bi-weekly)
    const lastPhase = Number(t?.scheduling?.lastScheduleChoice?.biWeeklyPhase);
    setBiWeeklyPhase([0, 1].includes(lastPhase) ? lastPhase : 0);

    // reset selections on open
    setWeekSelections({});
    setSingleDateIso("");
  }

  function closeTaskPanel() {
    setPanelOpen(false);
    setPanelTaskId(null);
    setBulkTaskIds([]); // ✅ clear bulk mode
    setSelectedEngineerId("");
    setWeekSelections({});
    setSkippedWeeks({});
    setSingleDateIso("");
  }

  const canScheduleWeekly = useMemo(() => {
    // if none of the selected tasks are weekly, ignore
    const weeklyTasks = panelTasks.filter((t) => (t?.cadence?.type || "monthly") === "weekly");
    if (!weeklyTasks.length) return true;

    // for each weekly task, required weeks must be selected OR skipped
    return weeklyTasks.every((t) => {
      const req = requiredWeeksForTask(t);
      return req.every((wi) => isWeekSkipped(wi) || Boolean(weekSelections?.[String(wi)]));
    });
  }, [panelTasks, weekSelections, skippedWeeks, eligibleWeekIndexes, biWeeklyPhase]);

  // Is monthly/quarterly/yearly ready? Must pick one date.
  const needsSingleDate = useMemo(() => {
    const type = panelTask?.cadence?.type;
    return type === "monthly" || type === "quarterly" || type === "yearly";
  }, [panelTask]);

  const canScheduleSingleDate = useMemo(() => {
    if (!panelTask) return true;
    if (!needsSingleDate) return true;
    return Boolean(singleDateIso);
  }, [panelTask, needsSingleDate, singleDateIso]);

  // build scheduleChoice (stored in task scheduling + job snapshot)
  const scheduleChoice = useMemo(() => {
    if (!panelTask) return null;

    const type = panelTask?.cadence?.type || "monthly";

    if (type === "daily") {
      // purely informational; daily is auto
      return { v: 1, kind: "daily-auto" };
    }

    if (type === "weekly") {
      const selectedDates = requiredWeekIndexes
        .map((wi) => weekSelections?.[String(wi)])
        .filter(Boolean);

      // patternWeekdays helps you apply “same vibe” next time if you want
      const patternWeekdays = selectedDates.map((ymd) => weekday0Sun(fromIsoDateLocal(ymd)));

      return {
        v: 1,
        kind: "weekly-calendar",
        interval: weeklyInterval,
        biWeeklyPhase: weeklyInterval === 2 ? biWeeklyPhase : null,
        requiredWeeks: requiredWeekIndexes,
        selectedDates,
        patternWeekdays,
      };
    }

    // monthly/quarterly/yearly: one selected date in the month
    if (type === "monthly") {
      return {
        v: 1,
        kind: "monthly-single",
        selectedDates: singleDateIso ? [singleDateIso] : [],
      };
    }
    if (type === "quarterly") {
      return {
        v: 1,
        kind: "quarterly-single",
        selectedDates: singleDateIso ? [singleDateIso] : [],
      };
    }
    if (type === "yearly") {
      return {
        v: 1,
        kind: "yearly-single",
        selectedDates: singleDateIso ? [singleDateIso] : [],
      };
    }

    return { v: 1, kind: "unknown" };
  }, [
    panelTask,
    weeklyInterval,
    biWeeklyPhase,
    requiredWeekIndexes,
    weekSelections,
    singleDateIso,
  ]);

  // due dates preview (this is the *actual* truth for jobs created)
  const dueDatesPreview = useMemo(() => {
    if (!panelTask) return [];

    const type = panelTask?.cadence?.type || "monthly";

    if (type === "daily") {
      return dueDatesForDaily(panelTask, year, month);
    }

    if (type === "weekly") {
      return weekSelectionsToDates(weekSelections, requiredWeekIndexes);
    }

    if (type === "monthly" || type === "quarterly" || type === "yearly") {
      return singleIsoToDates(singleDateIso);
    }

    return [];
  }, [panelTask, year, month, weekSelections, requiredWeekIndexes, singleDateIso]);

  async function reassignEngineerForTaskMonth(task) {
    if (!selectedAsset || !task) return;

    setErr("");
    setMsg("");

    const engineer = engineers.find((e) => e.id === selectedEngineerId) || null;
    if (!engineer) {
      setErr("Pick an engineer.");
      return;
    }

    setBusy(true);
    try {
      // update all jobs for this asset+task+month
      const jobsQ = query(
        collection(db, "ppmjobs"),
        where("assetId", "==", selectedAsset.id),
        where("taskId", "==", task.id),
        where("yearMonth", "==", ym)
      );

      const snap = await getDocs(jobsQ);

      for (const d of snap.docs) {
        await updateDoc(d.ref, {
          assignedEngineerId: engineer.id,
          assignedEngineerName: engineer.name || "",
          updatedAt: serverTimestamp(),
        });
      }

      // also update the task template "last engineer" memory (keep the month lock)
      const nextTasks = (Array.isArray(selectedAsset.tasks) ? selectedAsset.tasks : []).map((t) => {
        if (!t || t.id !== task.id) return t;

        const s = ensureTaskScheduling(t.scheduling);
        return {
          ...t,
          scheduling: {
            ...s,
            lastEngineerId: engineer.id,
            lastEngineerName: engineer.name || "",
            lastScheduledAt: Timestamp.now(),
            lastScheduledMonth: ym,
          },
        };
      });

      await updateDoc(doc(db, "ppmassets", selectedAsset.id), {
        tasks: nextTasks,
        updatedAt: serverTimestamp(),
      });

      setAssets((prev) => prev.map((a) => (a.id === selectedAsset.id ? { ...a, tasks: nextTasks } : a)));

      setMsg(`Reassigned "${task.name}" jobs for ${ym} to ${engineer.name}.`);
      closeTaskPanel();
    } catch (e) {
      setErr(e?.message || "Reassign failed.");
    } finally {
      setBusy(false);
    }
  }

  async function scheduleOneTaskForMonth() {
    if (!selectedAsset) return;
    if (!panelTask) return;

    setErr("");
    setMsg("");

    // ✅ Allow opening locked tasks so we can reassign engineer
    // Scheduling new jobs is still blocked later; but reassignment is allowed.

    const engineer = engineers.find((e) => e.id === selectedEngineerId) || null;
    if (!engineer) {
      setErr("Pick an engineer.");
      return;
    }

    const type = panelTask?.cadence?.type || "monthly";

    if (type === "weekly" && !canScheduleWeekly) {
      setErr("Weekly/bi-weekly: pick exactly one date in every required week.");
      return;
    }

    if (needsSingleDate && !canScheduleSingleDate) {
      setErr("Pick a date in the calendar for this month.");
      return;
    }

    const dueDates = dueDatesPreview.slice().sort((a, b) => a - b);
    if (!dueDates.length) {
      setErr("No dates selected / generated.");
      return;
    }

    setBusy(true);
    try {
      let created = 0;

      for (const d of dueDates) {
        const day = d.getDate();
        const jobId = `${selectedAsset.id}_${panelTask.id}_${ymdStringCompact(year, month, day)}`;
        const jobRef = doc(db, "ppmjobs", jobId);
        const snap = await getDoc(jobRef);
        if (snap.exists()) continue;

        await setDoc(jobRef, {
          assetId: selectedAsset.id,
          taskId: panelTask.id,
          yearMonth: ym,
          dueDate: Timestamp.fromDate(d),

          status: "open",
          createdAt: serverTimestamp(),

          assignedEngineerId: engineer.id,
          assignedEngineerName: engineer.name || "",

          // snapshots
          assetName: selectedAsset.name || "",
          assetCode: selectedAsset.code || "",
          taskName: panelTask.name || "",
          checklist: Array.isArray(panelTask.checklist) ? panelTask.checklist.filter(Boolean) : [],
          cadence: panelTask.cadence || null,

          // record how it was scheduled
          scheduleChoice: scheduleChoice || null,
        });

        created++;
      }

      const nextTasks = (Array.isArray(selectedAsset.tasks) ? selectedAsset.tasks : []).map((t) => {
        if (!t || t.id !== panelTask.id) return t;
        return lockMonthOnTask(
          t,
          year,
          month,
          { id: engineer.id, name: engineer.name || "" },
          scheduleChoice
        );
      });

      await updateDoc(doc(db, "ppmassets", selectedAsset.id), {
        tasks: nextTasks,
        updatedAt: serverTimestamp(), // OK (top-level, not inside tasks array)
      });

      setAssets((prev) => prev.map((a) => (a.id === selectedAsset.id ? { ...a, tasks: nextTasks } : a)));

      setMsg(`Scheduled "${panelTask.name}" for ${ym}. Jobs created: ${created}.`);
      closeTaskPanel();
    } catch (e) {
      setErr(e?.message || "Scheduling failed.");
    } finally {
      setBusy(false);
    }
  }

    async function scheduleManyTasksForMonth() {
    if (!selectedAsset) return;

    setErr("");
    setMsg("");

    const engineer = engineers.find((e) => e.id === selectedEngineerId) || null;
    if (!engineer) {
      setErr("Pick an engineer.");
      return;
    }

    const selected = panelTasks.filter((t) => t && t.id);
    if (!selected.length) {
      setErr("No tasks selected.");
      return;
    }

    // validation: weekly + single-date requirements must be satisfied for the selected set
    const hasWeekly = selected.some((t) => (t.cadence?.type || "monthly") === "weekly");
    if (hasWeekly && !canScheduleWeekly) {
      setErr("Weekly/bi-weekly: pick one date in each required week (or skip weeks).");
      return;
    }

    const hasSingle = selected.some((t) => ["monthly", "quarterly", "yearly"].includes(t.cadence?.type || "monthly"));
    if (hasSingle && !singleDateIso) {
      setErr("Pick a date in the calendar for this month (monthly/quarterly/yearly tasks).");
      return;
    }

    setBusy(true);
    try {
      let created = 0;

      // create jobs for each task
      for (const task of selected) {
        if (isMonthLocked(task, year, month)) continue; // skip already scheduled

        const dueDates = dueDatesForTask(task).slice().sort((a, b) => a - b);
        if (!dueDates.length) continue;

        const choice = buildScheduleChoiceForTask(task);

        for (const d of dueDates) {
          const day = d.getDate();
          const jobId = `${selectedAsset.id}_${task.id}_${ymdStringCompact(year, month, day)}`;
          const jobRef = doc(db, "ppmjobs", jobId);
          const snap = await getDoc(jobRef);
          if (snap.exists()) continue;

          await setDoc(jobRef, {
            assetId: selectedAsset.id,
            taskId: task.id,
            yearMonth: ym,
            dueDate: Timestamp.fromDate(d),

            status: "open",
            createdAt: serverTimestamp(),

            assignedEngineerId: engineer.id,
            assignedEngineerName: engineer.name || "",

            // snapshots
            assetName: selectedAsset.name || "",
            assetCode: selectedAsset.code || "",
            taskName: task.name || "",
            checklist: Array.isArray(task.checklist) ? task.checklist.filter(Boolean) : [],
            cadence: task.cadence || null,

            scheduleChoice: choice || null,
          });

          created++;
        }
      }

      // lock months on the underlying asset tasks
      const nextTasks = (Array.isArray(selectedAsset.tasks) ? selectedAsset.tasks : []).map((t) => {
        if (!t || !t.id) return t;
        const picked = selected.find((x) => String(x.id) === String(t.id));
        if (!picked) return t;

        if (isMonthLocked(picked, year, month)) return t;

        const choice = buildScheduleChoiceForTask(picked);
        return lockMonthOnTask(t, year, month, { id: engineer.id, name: engineer.name || "" }, choice);
      });

      await updateDoc(doc(db, "ppmassets", selectedAsset.id), {
        tasks: nextTasks,
        updatedAt: serverTimestamp(),
      });

      setAssets((prev) => prev.map((a) => (a.id === selectedAsset.id ? { ...a, tasks: nextTasks } : a)));

      setMsg(`Bulk scheduled ${selected.length} task(s) for ${ym}. Jobs created: ${created}.`);
      closeTaskPanel();
    } catch (e) {
      setErr(e?.message || "Bulk scheduling failed.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <div className="p-6 text-gray-700">Loading scheduler…</div>;
  }

  const panelType = panelTask?.cadence?.type || "";

  return (
    <div className="p-6 max-w-6xl space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">PPM Scheduling</h1>
          <p className="text-gray-600 mt-1">
            Select a month, choose a machine, then schedule tasks one-by-one. Month locks live on the task template.
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <div className="text-xs font-semibold text-gray-500">Year</div>
          <Select value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Select>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <div className="text-xs font-semibold text-gray-500">Month</div>
          <Select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>
                {monthOptionLabel(m)}
              </option>
            ))}
          </Select>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-3 md:col-span-2">
          <div className="text-xs font-semibold text-gray-500">Selected period</div>
          <div className="text-lg font-extrabold text-gray-900 mt-1">{ym}</div>
          <div className="text-sm text-gray-600">
            Unscheduled tasks for this machine: <span className="font-semibold">{unscheduledTasks.length}</span>
          </div>
        </div>
      </div>

      {err ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
          <div className="font-bold">Problem</div>
          <div className="mt-1">{err}</div>
        </div>
      ) : null}

      {msg ? (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-green-800">
          <div className="font-bold">OK</div>
          <div className="mt-1">{msg}</div>
        </div>
      ) : null}

      {/* Main layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Machines */}
        <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <div className="text-lg font-extrabold text-gray-900">Machines</div>
            <div className="text-sm text-gray-600">Pick one to schedule its tasks.</div>
          </div>

          <div className="max-h-[560px] overflow-auto">
            {assets.map((a) => {
              const active = a.active !== false;
              const isSel = a.id === selectedAssetId;

              const t = Array.isArray(a.tasks) ? a.tasks : [];
              const activeTasks = t
                .filter((x) => x && x.id && x.active !== false)
                .filter((x) => isTaskApplicableInMonth(x, year, month));
              const uns = activeTasks.filter((x) => !isMonthLocked(x, year, month)).length;

              return (
                <div
                  key={a.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedAssetId(a.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setSelectedAssetId(a.id);
                  }}
                  className={cn(
                    "px-4 py-3 border-b border-gray-100 cursor-pointer select-none",
                    isSel ? "bg-purple-50" : "hover:bg-gray-50",
                    !active && "opacity-60"
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-extrabold text-gray-900">{a.name || "Unnamed machine"}</div>
                      <div className="text-xs text-gray-500 mt-1">{a.code || a.id}</div>
                    </div>
                    <div className="text-xs font-semibold text-gray-700">{uns} unscheduled</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tasks */}
        <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-lg font-extrabold text-gray-900">
                  Tasks — {selectedAsset?.name || "No machine selected"}
                </div>
                <div className="text-sm text-gray-600">
                  Schedule tasks one-by-one or select many for <span className="font-semibold">{ym}</span>.
                </div>
              </div>

              <div className="flex gap-2">
                <GhostButton
                  type="button"
                  disabled={!unscheduledTasks.length || busy}
                  onClick={() => {
                    // select all unscheduled tasks
                    setBulkTaskIds(unscheduledTasks.map((t) => t.id));
                    setPanelTaskId(null);
                    setPanelOpen(true);

                    // defaults
                    setSelectedEngineerId(unscheduledTasks[0]?.scheduling?.lastEngineerId || "");
                    setWeekSelections({});
                    setSkippedWeeks({});
                    setSingleDateIso("");

                    // restore last phase if present
                    const lastPhase = Number(unscheduledTasks[0]?.scheduling?.lastScheduleChoice?.biWeeklyPhase);
                    setBiWeeklyPhase([0, 1].includes(lastPhase) ? lastPhase : 0);
                  }}
                >
                  Select all unscheduled
                </GhostButton>

                <Button
                  type="button"
                  disabled={!unscheduledTasks.length || busy}
                  onClick={() => {
                    // open bulk panel (same as select-all above)
                    setBulkTaskIds(unscheduledTasks.map((t) => t.id));
                    setPanelTaskId(null);
                    setPanelOpen(true);

                    setSelectedEngineerId(unscheduledTasks[0]?.scheduling?.lastEngineerId || "");
                    setWeekSelections({});
                    setSkippedWeeks({});
                    setSingleDateIso("");

                    const lastPhase = Number(unscheduledTasks[0]?.scheduling?.lastScheduleChoice?.biWeeklyPhase);
                    setBiWeeklyPhase([0, 1].includes(lastPhase) ? lastPhase : 0);
                  }}
                >
                  Schedule all
                </Button>
              </div>
            </div>
          </div>

          {!selectedAsset ? (
            <div className="p-6 text-gray-600">Select a machine to view tasks.</div>
          ) : tasks.length === 0 ? (
            <div className="p-6 text-gray-600">No applicable tasks in this month.</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {tasks.map((t) => {
                const locked = isMonthLocked(t, year, month);
                const s = ensureTaskScheduling(t.scheduling);
                const cadenceType = t.cadence?.type || "monthly";

                return (
                  <div key={t.id} className="p-4 flex items-center justify-between gap-3">
                    <div>
                      <div className="font-extrabold text-gray-900">{t.name?.trim() ? t.name : "Untitled task"}</div>

                      <div className="text-xs text-gray-500 mt-1">
                        Cadence: <span className="font-semibold">{cadenceType}</span>
                        {cadenceType === "weekly" ? (
                          <span className="ml-2 text-gray-600">
                            ({Number(t.cadence?.interval) === 2 ? "Bi-weekly" : "Weekly"})
                          </span>
                        ) : null}
                        {" · "}
                        {locked ? (
                          <span className="text-green-700 font-semibold">Scheduled ({pad2(month)}/{year})</span>
                        ) : (
                          <span className="text-red-700 font-semibold">Not scheduled ({pad2(month)}/{year})</span>
                        )}
                      </div>

                      {s.lastEngineerName ? (
                        <div className="text-xs text-gray-500 mt-1">
                          Last assigned: <span className="font-semibold">{s.lastEngineerName}</span>
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400 mt-1">No previous assignment yet.</div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <GhostButton type="button" onClick={() => openTaskPanel(t.id)} disabled={busy}>
                        {locked ? "View / Reassign" : "Schedule"}
                      </GhostButton>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Scheduling panel */}
      {panelOpen && panelTask ? (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-5xl rounded-2xl bg-white border border-gray-200 shadow-xl overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-gray-500">Schedule task for {ym}</div>
                <div className="text-xl font-extrabold text-gray-900">{panelTask.name || "Untitled task"}</div>
                <div className="text-sm text-gray-600 mt-1">
                  Creates <span className="font-semibold">{dueDatesPreview.length}</span> job(s) in{" "}
                  <span className="font-semibold">ppmjobs</span>, then locks this month on the task.
                </div>
              </div>
              <GhostButton onClick={closeTaskPanel} disabled={busy}>
                Close
              </GhostButton>
            </div>

            <div className="p-4 space-y-4">
              {suggestedEngineer ? (
                <div className="rounded-xl border border-purple-200 bg-purple-50 p-3">
                  <div className="text-sm font-semibold text-purple-900">Suggestion</div>
                  <div className="text-sm text-purple-900 mt-1">
                    Last time you assigned <span className="font-extrabold">{suggestedEngineer.name}</span>. Reuse?
                  </div>
                  <div className="mt-2">
                    <Button
                      type="button"
                      onClick={() => setSelectedEngineerId(suggestedEngineer.id)}
                      disabled={busy}
                    >
                      Use {suggestedEngineer.name}
                    </Button>
                  </div>
                </div>
              ) : null}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Left */}
                <div className="space-y-4">
                  <div className="rounded-xl border border-gray-200 p-3">
                    <div className="text-sm font-semibold text-gray-800">Assign engineer</div>
                    <Select value={selectedEngineerId} onChange={(e) => setSelectedEngineerId(e.target.value)}>
                      <option value="">Select engineer…</option>
                      {engineers.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.name || e.id}
                        </option>
                      ))}
                    </Select>
                    <div className="text-xs text-gray-500 mt-2">Applied to every job generated for this task.</div>
                  </div>

                  {/* Bi-weekly phase */}
                  {panelType === "weekly" && weeklyInterval === 2 ? (
                    <div className="rounded-xl border border-gray-200 p-3">
                      <div className="text-sm font-semibold text-gray-800">Bi-weekly start</div>
                      <div className="text-xs text-gray-500 mt-1">
                        Choose which set of weeks you want to schedule in this month.
                      </div>

                      <div className="mt-2 flex gap-2">
                        <button
                          type="button"
                          className={cn(
                            "px-3 py-2 rounded-lg border text-sm font-semibold transition",
                            biWeeklyPhase === 0
                              ? "bg-purple-600 text-white border-purple-600"
                              : "bg-white text-gray-800 border-gray-200 hover:bg-gray-50"
                          )}
                          onClick={() => {
                            setBiWeeklyPhase(0);
                            setWeekSelections({});
                          }}
                          disabled={busy}
                        >
                          Set A (Week 1,3,5…)
                        </button>

                        <button
                          type="button"
                          className={cn(
                            "px-3 py-2 rounded-lg border text-sm font-semibold transition",
                            biWeeklyPhase === 1
                              ? "bg-purple-600 text-white border-purple-600"
                              : "bg-white text-gray-800 border-gray-200 hover:bg-gray-50"
                          )}
                          onClick={() => {
                            setBiWeeklyPhase(1);
                            setWeekSelections({});
                          }}
                          disabled={busy}
                        >
                          Set B (Week 2,4…)
                        </button>
                      </div>
                    </div>
                  ) : null}

                  <div className="rounded-xl border border-gray-200 p-3">
                    <div className="text-sm font-semibold text-gray-800">Preview due dates</div>
                    {dueDatesPreview.length ? (
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {dueDatesPreview
                          .slice()
                          .sort((a, b) => a - b)
                          .slice(0, 12)
                          .map((d) => (
                            <div key={d.toISOString()} className="text-xs rounded-lg border border-gray-200 px-2 py-1">
                              {toIsoDateLocal(d)}
                            </div>
                          ))}
                        {dueDatesPreview.length > 12 ? (
                          <div className="text-xs text-gray-500 col-span-2">…and {dueDatesPreview.length - 12} more</div>
                        ) : null}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-600 mt-2">
                        {panelType === "daily"
                          ? "No days configured (or none match)."
                          : panelType === "weekly"
                          ? "Pick dates on the calendar (one per required week)."
                          : "Pick a date on the calendar for this month."}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: calendar */}
                <div className="rounded-xl border border-gray-200 p-3">
                  <div className="text-sm font-semibold text-gray-800">
                    Calendar — {monthOptionLabel(month)} {year}
                  </div>

                  {/* Calendar header */}
                  <div className="mt-3 grid grid-cols-7 gap-2 text-xs font-semibold text-gray-500 mb-2">
                    <div>Mon</div>
                    <div>Tue</div>
                    <div>Wed</div>
                    <div>Thu</div>
                    <div>Fri</div>
                    <div>Sat</div>
                    <div>Sun</div>
                  </div>

                  {/* Daily: no manual calendar */}
                  {panelType === "daily" ? (
                    <div className="text-sm text-gray-600 mt-2">Daily uses the template days-of-week; no manual picks.</div>
                  ) : panelType === "weekly" ? (
                    // WEEKLY / BI-WEEKLY: one pick per required week
                    <div className="space-y-2">
                      {monthWeeks.map((w) => {
                        if (!w.anyInMonth) return null;

                        const allowed = requiredWeekIndexes.includes(w.weekIndex);
                        const complete = isWeekComplete(w.weekIndex);

                        return (
                          <div
                            key={w.weekIndex}
                            className={cn(
                              "rounded-lg border p-2",
                              allowed ? "bg-white border-gray-200" : "bg-gray-50 border-gray-200 opacity-60"
                            )}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="text-xs font-semibold text-gray-700">
                                Week {w.weekIndex + 1}{" "}
                                {!allowed ? "(blocked)" : complete ? `— selected ${weekSelections[String(w.weekIndex)]}` : ""}
                              </div>

                                                            {allowed ? (
                                <div className="flex items-center gap-2">
                                  {isWeekSkipped(w.weekIndex) ? (
                                    <span className="text-xs font-semibold text-gray-600">Skipped</span>
                                  ) : complete ? (
                                    <span className="text-xs font-semibold text-gray-600">
                                      Selected {weekSelections[String(w.weekIndex)]}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-gray-500">Pick one day (or skip)</span>
                                  )}

                                  {isWeekSkipped(w.weekIndex) ? (
                                    <GhostButton
                                      type="button"
                                      className="px-2 py-1 text-xs"
                                      disabled={busy}
                                      onClick={() => toggleSkipWeek(w.weekIndex)}
                                    >
                                      Unskip
                                    </GhostButton>
                                  ) : complete ? (
                                    <GhostButton
                                      type="button"
                                      className="px-2 py-1 text-xs"
                                      disabled={busy}
                                      onClick={() => clearWeekSelection(w.weekIndex)}
                                    >
                                      Change
                                    </GhostButton>
                                  ) : (
                                    <GhostButton
                                      type="button"
                                      className="px-2 py-1 text-xs"
                                      disabled={busy}
                                      onClick={() => toggleSkipWeek(w.weekIndex)}
                                    >
                                      Skip week
                                    </GhostButton>
                                  )}
                                </div>
                              ) : (
                                <div className="text-xs text-gray-500">Unavailable</div>
                              )}
                            </div>

                            <div className="grid grid-cols-7 gap-2">
                              {w.days.map(({ date, inMonth }) => {
                                const selected = weekSelections[String(w.weekIndex)] === isoDate(date);

                                const clickable =
                                  inMonth && allowed && !complete && !busy && !isWeekSkipped(w.weekIndex);

                                return (
                                  <button
                                    key={date.toISOString()}
                                    type="button"
                                    disabled={!clickable}
                                    onClick={() => selectDateForWeek(w.weekIndex, date)}
                                    className={cn(
                                      "h-10 rounded-lg border text-sm font-semibold transition",
                                      inMonth ? "border-gray-200 bg-white" : "border-transparent bg-transparent opacity-30",
                                      clickable && "hover:bg-gray-50",
                                      selected && "bg-purple-100 text-purple-800 border-purple-300 hover:bg-purple-100"

                                    )}
                                    title={isoDate(date)}
                                  >
                                    {inMonth ? date.getDate() : ""}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    // MONTHLY / QUARTERLY / YEARLY: pick ONE date in the month
                    <div className="space-y-2">
                      {monthWeeks.map((w) => {
                        if (!w.anyInMonth) return null;

                        return (
                          <div key={w.weekIndex} className="rounded-lg border border-gray-200 p-2 bg-white">
                            <div className="grid grid-cols-7 gap-2">
                              {w.days.map(({ date, inMonth }) => {
                                const selected = singleDateIso === isoDate(date);
                                const clickable = inMonth && !busy;

                                return (
                                  <button
                                    key={date.toISOString()}
                                    type="button"
                                    disabled={!clickable}
                                    onClick={() => setSingleDateIso(isoDate(date))}
                                    className={cn(
                                      "h-10 rounded-lg border text-sm font-semibold transition",
                                      inMonth ? "border-gray-200 bg-white" : "border-transparent bg-transparent opacity-30",
                                      clickable && "hover:bg-gray-50",
                                      selected && "bg-purple-100 text-purple-800 border-purple-300 hover:bg-purple-100"

                                    )}
                                    title={isoDate(date)}
                                  >
                                    {inMonth ? date.getDate() : ""}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}

                      <div className="text-xs text-gray-500">
                        {panelType === "monthly"
                          ? "Monthly: pick one date in this month."
                          : panelType === "quarterly"
                          ? "Quarterly: pick one date in this month (month is predetermined by quarter settings)."
                          : panelType === "yearly"
                          ? "Yearly: pick one date in this month (month predetermined by task)."
                          : ""}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {err ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
                  <div className="font-bold">Problem</div>
                  <div className="mt-1">{err}</div>
                </div>
              ) : null}
            </div>

            <div className="p-4 border-t border-gray-100 flex justify-end gap-2">
              <GhostButton onClick={closeTaskPanel} disabled={busy}>
                Cancel
              </GhostButton>

                            {(() => {
                const single = !isBulk ? panelTasks[0] : null;
                const lockedSingle = single ? isMonthLocked(single, year, month) : false;

                const scheduleDisabled =
                  busy ||
                  !selectedEngineerId ||
                  (!isBulk && lockedSingle) || // locked tasks can't create new jobs
                  (panelTasks.some((t) => (t.cadence?.type || "monthly") === "weekly") && !canScheduleWeekly) ||
                  (panelTasks.some((t) => ["monthly","quarterly","yearly"].includes(t.cadence?.type || "")) && !singleDateIso);

                if (!isBulk && lockedSingle) {
                  return (
                    <Button
                      onClick={() => reassignEngineerForTaskMonth(single)}
                      disabled={busy || !selectedEngineerId}
                    >
                      {busy ? "Reassigning…" : "Reassign engineer"}
                    </Button>
                  );
                }

                return (
                  <Button
                    onClick={isBulk ? scheduleManyTasksForMonth : scheduleOneTaskForMonth}
                    disabled={scheduleDisabled}
                  >
                    {busy ? "Scheduling…" : isBulk ? "Schedule selected tasks" : "Schedule task now"}
                  </Button>
                );
              })()}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
